import { Search } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";

import { UserFormData } from "../model/schemas";

import { CreateUserDTORole } from "@/common/api";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  Textarea,
} from "@/common/components";
import { ROLE_LABELS } from "@/common/model";

const ROLE_OPTIONS = [
  {
    value: CreateUserDTORole.CARGO_OWNER,
    label: ROLE_LABELS.cargo_owner,
  },
  {
    value: CreateUserDTORole.WAGON_OWNER,
    label: ROLE_LABELS.wagon_owner,
  },
  {
    value: CreateUserDTORole.ADMIN,
    label: ROLE_LABELS.admin,
  },
] as const;

type UserFormMode = "create" | "edit";

interface UserFormProps {
  mode: UserFormMode;
  form: UseFormReturn<UserFormData>;
  onSubmit: (data: UserFormData) => void;
  isSubmitting: boolean;
  submitLabel: string;
  submittingLabel: string;
  onCancel: () => void;

  /** Только для режима создания — поиск компании по ИНН */
  onSearchCompany?: () => void;
  isSearchingCompany?: boolean;
}

export const UserForm = function UserForm({
  mode,
  form,
  onSubmit,
  isSubmitting,
  submitLabel,
  submittingLabel,
  onCancel,
  onSearchCompany,
  isSearchingCompany,
}: UserFormProps) {
  const isCreate = mode === "create";
  const isEdit = mode === "edit";

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" autoComplete="off">
        {/* Логин и Пароль */}
        <div
          className={
            isCreate ? "grid grid-cols-2 gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-4 items-end"
          }
        >
          <FormField
            control={form.control}
            name="login"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{isCreate ? "Логин *" : "Логин"}</FormLabel>
                <FormControl>
                  <Input {...(field as any)} disabled={isEdit} autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="passwordHash"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{`Пароль${isCreate ? " *" : ""}`}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    {...(field as any)}
                    autoComplete={isCreate ? "new-password" : "current-password"}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Имя, Роль, Email, Телефон */}
        <div
          className={isCreate ? "grid grid-cols-2 gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}
        >
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Полное имя *</FormLabel>
                <FormControl>
                  <Input {...(field as any)} autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Роль *</FormLabel>
                <Select
                  key={String(field.value)}
                  value={String(field.value || "")}
                  onValueChange={field.onChange}
                  disabled={isEdit}
                  {...(isCreate
                    ? {
                        defaultValue: ROLE_OPTIONS.map((o) => o.value)[0],
                        placeholder: "Выберите роль",
                      }
                    : {})}
                >
                  <SelectContent>
                    {ROLE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input type="email" {...(field as any)} autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contactNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Телефон *</FormLabel>
                <FormControl>
                  <Input {...(field as any)} autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Данные компании */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Данные компании</h3>

          {/* Поиск по ИНН — только в режиме создания */}
          {isCreate && onSearchCompany && (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Введите ИНН для поиска компании"
                  className="pl-9"
                  value={(form.watch("inn") as string) || ""}
                  onChange={(e) => {
                    form.setValue("inn", e.target.value);
                  }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={isSearchingCompany}
                onClick={onSearchCompany}
              >
                <Search className="h-4 w-4 mr-2" />
                {isSearchingCompany ? "Поиск..." : "Найти"}
              </Button>
            </div>
          )}

          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {isEdit ? "Наименование компании *" : "Наименование компании"}
                </FormLabel>
                <FormControl>
                  <Input {...(field as any)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div
            className={
              isCreate ? "grid grid-cols-2 gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"
            }
          >
            <FormField
              control={form.control}
              name="inn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ИНН *</FormLabel>
                  <FormControl>
                    <Input {...(field as any)} disabled={isEdit} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="kpp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>КПП *</FormLabel>
                  <FormControl>
                    <Input {...(field as any)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="ogrn"
            render={({ field }) => (
              <FormItem>
                <FormLabel>ОГРН *</FormLabel>
                <FormControl>
                  <Input {...(field as any)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div
            className={
              isCreate ? "grid grid-cols-2 gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"
            }
          >
            <FormField
              control={form.control}
              name="companyEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email компании</FormLabel>
                  <FormControl>
                    <Input type="email" {...(field as any)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyContactNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Телефон компании *</FormLabel>
                  <FormControl>
                    <Input {...(field as any)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="addressLegal"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{isCreate ? "Юридический адрес *" : "Юридический адрес"}</FormLabel>
                <FormControl>
                  <Textarea {...(field as any)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="addressActual"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{isCreate ? "Фактический адрес *" : "Фактический адрес"}</FormLabel>
                <FormControl>
                  <Textarea {...(field as any)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Кнопки */}
        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Отмена
          </Button>
          <Button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};
